import React, { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { AdminLayout } from "@/pages/admin/AdminLayout";
import { Textarea } from "@/components/ui/textarea";
import { Sparkles, Loader2, AlertTriangle } from "lucide-react";

const SUGGESTIONS = [
  "Create an Africa landing page for the Bachelor in AI, in French.",
  "Draft an email sequence for applicants who abandon the assessment.",
  "Create a new master's assessment question on causal inference.",
  "Draft an investor update for the pre-launch priority list.",
  "Build a WhatsApp follow-up for unpaid admitted students.",
];

export default function AILab() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sid, setSid] = useState(null);

  const send = async (text) => {
    const prompt = text || input;
    if (!prompt.trim()) return;
    setMessages((m) => [...m, { role: "user", text: prompt }]);
    setInput(""); setLoading(true);
    try {
      const { data } = await api.post("/admin/ai", { prompt, session_id: sid, context: "AI command centre" });
      setSid(data.session_id);
      setMessages((m) => [...m, { role: "assistant", text: data.reply, confirm: data.requires_confirmation }]);
    } catch (err) {
      setMessages((m) => [...m, { role: "assistant", text: formatApiErrorDetail(err.response?.data?.detail) || "AI request failed.", error: true }]);
    } finally { setLoading(false); }
  };

  return (
    <AdminLayout>
      <div className="p-8 md:p-10 max-w-4xl">
        <div className="eyebrow text-heraldic mb-1 flex items-center gap-2"><Sparkles className="h-3.5 w-3.5" /> Simo AI</div>
        <h1 className="font-serif text-4xl text-paper">Administrative command centre</h1>
        <p className="text-paper/50 text-sm mt-1">Powered by Claude Sonnet 4.6. Simo AI creates drafts and proposals; high-impact actions require your explicit confirmation. It never invents accreditation, faculty, partners, outcomes or investments.</p>

        {messages.length === 0 && (
          <div className="mt-8 grid sm:grid-cols-2 gap-2">
            {SUGGESTIONS.map((s) => (
              <button key={s} data-testid="ai-suggestion" onClick={() => send(s)} className="text-left text-sm border border-white/10 rounded-sm p-3 text-paper/70 hover:bg-white/5 hover:text-paper transition-colors">{s}</button>
            ))}
          </div>
        )}

        <div className="mt-8 space-y-4">
          {messages.map((m, i) => (
            <div key={i} className={`rounded-sm p-4 ${m.role === "user" ? "bg-heraldic/15 border border-heraldic/30 ml-auto max-w-[85%]" : "bg-white/[0.03] border border-white/10"}`}>
              {m.confirm && <div className="flex items-center gap-2 text-amber-400 text-xs mb-2"><AlertTriangle className="h-3.5 w-3.5" /> Confirmation required before this takes effect</div>}
              <div className={`whitespace-pre-wrap text-sm ${m.error ? "text-red-400" : "text-paper/90"}`}>{m.text}</div>
            </div>
          ))}
          {loading && <div className="flex items-center gap-2 text-paper/50 text-sm"><Loader2 className="h-4 w-4 animate-spin" /> Simo AI is thinking…</div>}
        </div>

        <div className="mt-6 sticky bottom-6">
          <div className="flex gap-2">
            <Textarea data-testid="ai-input" rows={2} value={input} onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder="Ask Simo AI to build a programme, campaign, funnel, email, assessment question…"
              className="bg-[#14161c] border-white/10 text-paper rounded-sm" />
            <button data-testid="ai-send" onClick={() => send()} disabled={loading} className="bg-heraldic text-white px-6 rounded-sm hover:bg-heraldic-700 transition-colors disabled:opacity-50">Send</button>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
